// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file CodeBlock.tsx
 * @input Uses React, StyleX, theme tokens, CSS Custom Highlight API, SyntaxTheme provider
 * @output Exports CodeBlock component and CodeBlockProps
 * @position Core implementation; read-only syntax-highlighted code display
 */
import { useInsertionEffect, useEffect, useId, useRef, useState, useCallback, useMemo } from 'react';
import * as React from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, typographyVars, fontWeightVars, typeScaleVars, borderVars, durationVars, easeVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { useAnnounce } from "../hooks/useAnnounce.js";
import { Icon } from "../Icon/index.js";
import { tokenize, tokenizeAsync, flatTokensToLines, SYNC_TOKENIZE_THRESHOLD } from "./tokenizer.js";
import { ensureHighlightStyles } from "./highlightStyles.js";
import { applyHighlightRangesChunked } from "./highlightRanges.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";
import { SyntaxTheme } from "../theme/syntax/index.js";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const containerStyles = {
  card: {
    kaIpWk: "xh6dtrn",
    kMzoRj: "x1litavf",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x14i3s5s",
    $$css: true
  },
  section: {
    kaIpWk: "x2u8bby",
    kMzoRj: "xc342km",
    ksu8eU: "xng3xce",
    kVAM5u: "x9r1u3d",
    "--color-syntax-background": "x1mtvhk1",
    $$css: true
  }
};
const _temp = {
  "--_codeblock-gutter-width": "xtk08jq",
  "$$css": true
};
const dynamicStyles = {
  width: value => [{
    kzqmXN: value != null ? "x5lhr3w" : value,
    k7Eaqz: (value === 'fit-content' ? 'min(100%, 400px)' : null) != null ? "xkj4a21" : value === 'fit-content' ? 'min(100%, 400px)' : null,
    ks0D6T: (value === 'fit-content' ? '100%' : null) != null ? "xf68679" : value === 'fit-content' ? '100%' : null,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(value),
    "--x-minWidth": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(value === 'fit-content' ? 'min(100%, 400px)' : null),
    "--x-maxWidth": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(value === 'fit-content' ? '100%' : null)
  }],
  gutterWidth: digits => [_temp, {
    "--x---_codeblock-gutter-width": `${digits}ch` != null ? `${digits}ch` : undefined
  }]
};
const styles = {
  root: {
    kVAEAm: "x1n2onr6",
    kHBbk8: "xc8icb0",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kogj98: "x1ghz6dp",
    kWkggS: "xzyc27v",
    kVQacm: "xb3r6kr",
    $$css: true
  },
  code: {
    k1xSpc: "x1lliihq",
    kUk6DE: "x98rzlu",
    k8WAf4: "x8o8v82",
    kg3NbH: "x1pzlopt",
    kogj98: "x1ghz6dp",
    kMv6JI: "x9m5x89",
    kMwMTN: "xdfisg",
    kKi2Bq: "xeq4nuv",
    khDVqt: "x1sdyfia",
    kTgw9: "x1lldw8n",
    kHjlTd: "x1h4wwuj",
    $$css: true
  },
  codeWrapped: {
    khDVqt: "x126k92a",
    kTgw9: "x1yn0g08",
    kHjlTd: "x1mzt3pk",
    $$css: true
  },
  codeNumbered: {
    kVAEAm: "x1n2onr6",
    k5JduY: "x1s928wv",
    kwXMNM: "x1j6awrg",
    kSy8m5: "xd54j2p",
    kc1e00: "x1km86hw",
    kkgrvl: "xkj6t0a",
    kQlJh0: "x1p96cux",
    k6vLHU: "xvsnedh",
    kkQ525: "xnnzo76",
    kloYau: "x2q1x1w",
    $$css: true
  },
  sizeSm: {
    kGuDYH: "x141an7d",
    $$css: true
  },
  sizeMd: {
    kGuDYH: "xp03k98",
    $$css: true
  }
};

// ---------------------------------------------------------------------------
// Line rendering
// ---------------------------------------------------------------------------

const LINE_CHUNK_SIZE = 20;
const LINE_CHUNK_THRESHOLD = 100;

/**
 * Memoized chunk component — cheaper than memoizing every individual line.
 */
const CodeChunk = /*#__PURE__*/React.memo(function CodeChunk({
  lines,
  startIndex,
  highlightSet,
  renderLineContent,
  lineNumbers
}) {
  return /*#__PURE__*/_jsx(_Fragment, {
    children: lines.map((line, j) => {
      const i = startIndex + j;
      return /*#__PURE__*/_jsx("div", {
        "data-line": i + 1,
        ...{
          0: {
            className: "x17iicif"
          },
          2: {
            className: "x17iicif xrvj5dj x1xx4m37 xf5zn4v x46gnpd x17gn2l9 x19ds33d x1wnbjh8 xtkarl4 x1rbqspl x3nzp0f"
          },
          1: {
            className: "x17iicif xgcxg3y x1ng9b9t x1pzlopt"
          },
          3: {
            className: "x17iicif xrvj5dj x1xx4m37 xf5zn4v x46gnpd x17gn2l9 x19ds33d x1wnbjh8 xtkarl4 x1rbqspl x3nzp0f xgcxg3y x1ng9b9t x1pzlopt"
          }
        }[!!lineNumbers << 1 | !!(highlightSet?.has(i + 1) ?? false) << 0],
        children: renderLineContent(line, i)
      }, i);
    })
  });
});
function renderLines(lines, highlightSet, renderLineContent, lineNumbers, chunkSize = LINE_CHUNK_SIZE) {
  chunkSize = Math.max(1, Math.floor(chunkSize));
  if (lines.length < LINE_CHUNK_THRESHOLD) {
    return /*#__PURE__*/_jsx(CodeChunk, {
      lines: lines,
      startIndex: 0,
      highlightSet: highlightSet,
      renderLineContent: renderLineContent,
      lineNumbers: lineNumbers
    });
  }
  const chunks = [];
  for (let start = 0; start < lines.length; start += chunkSize) {
    const end = Math.min(start + chunkSize, lines.length);
    const chunkLines = lines.slice(start, end);
    const estimatedHeight = `${chunkLines.length}lh`;
    chunks.push(/*#__PURE__*/_jsx("div", {
      ...mergeProps({
        className: "xb5mbof"
      }, {
        style: {
          containIntrinsicBlockSize: `auto ${estimatedHeight}`
        }
      }),
      children: /*#__PURE__*/_jsx(CodeChunk, {
        lines: chunkLines,
        startIndex: start,
        highlightSet: highlightSet,
        renderLineContent: renderLineContent,
        lineNumbers: lineNumbers
      })
    }, start));
  }
  return chunks;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hasHighlightAPI() {
  return typeof CSS !== 'undefined' && 'highlights' in CSS && typeof Highlight !== 'undefined';
}

/**
 * Safari supports the Highlight API JS objects but has rendering issues
 * with ::highlight() in code blocks. Detect Safari (WebKit without Chrome)
 * so we can fall back to spans.
 */
function isSafari() {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  return ua.includes('AppleWebKit') && !ua.includes('Chrome');
}

/**
 * Hook: per-line tokens with sync/async + custom tokenizer compat.
 */
function useTokenLines(code, language, customTokenizer) {
  const [asyncTokenResult, setAsyncTokenResult] = useState(null);
  const syncTokens = useMemo(() => {
    if (customTokenizer) {
      return flatTokensToLines(customTokenizer(code, language), code);
    }
    if (code.length >= SYNC_TOKENIZE_THRESHOLD) {
      return null;
    }
    return tokenize(code, language);
  }, [code, language, customTokenizer]);
  useEffect(() => {
    if (code.length < SYNC_TOKENIZE_THRESHOLD || customTokenizer) {
      return;
    }
    const abortController = new AbortController();
    async function tokenizeLargeCode() {
      try {
        const tokens = await tokenizeAsync(code, language, abortController.signal);
        if (!abortController.signal.aborted) {
          setAsyncTokenResult({
            code,
            language,
            tokens
          });
        }
      } catch {
        if (!abortController.signal.aborted) {
          setAsyncTokenResult({
            code,
            language,
            tokens: []
          });
        }
      }
    }
    void tokenizeLargeCode();
    return () => {
      abortController.abort();
    };
  }, [code, language, customTokenizer]);
  if (syncTokens != null) {
    return syncTokens;
  }
  if (asyncTokenResult?.code === code && asyncTokenResult.language === language) {
    return asyncTokenResult.tokens;
  }
  return [];
}

// ---------------------------------------------------------------------------
// Span-mode code element
// ---------------------------------------------------------------------------

function buildSpanLine(lineText, tokens) {
  if (tokens.length === 0) {
    return lineText || '\u200b';
  }
  const parts = [];
  let cursor = 0;
  for (const token of tokens) {
    if (token.start > cursor) {
      parts.push(lineText.slice(cursor, token.start));
    }
    const end = Math.min(token.end, lineText.length);
    parts.push(/*#__PURE__*/_jsx("span", {
      className: `astryx-token-${token.type} xds-token-${token.type}`,
      children: lineText.slice(token.start, end)
    }, `${token.start}-${token.type}`));
    cursor = end;
  }
  if (cursor < lineText.length) {
    parts.push(lineText.slice(cursor));
  }
  return parts.length > 0 ? parts : '\u200b';
}
function SpanCodeContent({
  lines,
  tokenLines,
  highlightSet,
  isWrapped,
  sizeStyle,
  hasLineNumbers,
  maxDigits
}) {
  useInsertionEffect(() => {
    ensureHighlightStyles();
  }, []);
  const renderLineContent = useCallback((line, lineIndex) => {
    const tokens = tokenLines[lineIndex] ?? [];
    // Wrap tokens in a single element so they occupy one grid cell when line
    // numbers are on (see `lineNumbered`); an inline span is a no-op when off.
    return /*#__PURE__*/_jsx("span", {
      ...{
        className: "xeuugli"
      },
      children: buildSpanLine(line, tokens)
    });
  }, [tokenLines]);
  return /*#__PURE__*/_jsx("code", {
    ...stylex.props(styles.code, sizeStyle, isWrapped && styles.codeWrapped, hasLineNumbers && styles.codeNumbered, hasLineNumbers && dynamicStyles.gutterWidth(maxDigits)),
    children: renderLines(lines, highlightSet, renderLineContent, hasLineNumbers)
  });
}

// ---------------------------------------------------------------------------
// Range-mode code element
// ---------------------------------------------------------------------------

function RangeCodeContent({
  lines,
  tokenLines,
  highlightSet,
  isWrapped,
  sizeStyle,
  hasLineNumbers,
  maxDigits
}) {
  const codeRef = useRef(null);
  useEffect(() => {
    if (!hasHighlightAPI()) {
      return;
    }
    ensureHighlightStyles();
    const codeEl = codeRef.current;
    if (!codeEl || tokenLines.length === 0) {
      return;
    }
    return applyHighlightRangesChunked(codeEl, tokenLines);
  }, [tokenLines]);

  // Range mode keeps the line's text as a bare text node (its firstChild) so
  // applyHighlightRangesChunked can map token offsets onto it \u2014 no wrapper. The
  // number ::before is a pseudo-element, so it never becomes a child node here.
  const renderLineContent = useCallback(line => line || '\u200b', []);
  return /*#__PURE__*/_jsx("code", {
    ref: codeRef,
    ...stylex.props(styles.code, sizeStyle, isWrapped && styles.codeWrapped, hasLineNumbers && styles.codeNumbered, hasLineNumbers && dynamicStyles.gutterWidth(maxDigits)),
    children: renderLines(lines, highlightSet, renderLineContent, hasLineNumbers)
  });
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Read-only code display with syntax highlighting, line numbers,
 * and optional copy button.
 *
 * @example
 * ```
 * <CodeBlock code="const x = 42;" language="javascript" />
 * ```
 */
export function CodeBlock({
  code,
  language = 'plaintext',
  title,
  hasLanguageLabel = true,
  hasLineNumbers = false,
  highlightLines,
  hasCopyButton = true,
  onCopy,
  isWrapped = false,
  maxHeight,
  isCollapsible = false,
  collapsibleThreshold = 10,
  size = 'md',
  width: widthProp = 'fit-content',
  container = 'card',
  tokenizer: customTokenizer,
  highlightMode = 'auto',
  syntaxTheme,
  xstyle,
  className,
  style,
  ref,
  ...props
}) {
  const t = useTranslator();
  const [copied, setCopied] = useState(false);
  const copyResetTimerRef = useRef(null);
  const announce = useAnnounce();

  // Clear a pending "copied" reset when the block unmounts.
  useEffect(() => {
    return () => {
      if (copyResetTimerRef.current != null) {
        clearTimeout(copyResetTimerRef.current);
      }
    };
  }, []);
  const useSpans = highlightMode === 'spans' || highlightMode === 'auto' && !hasHighlightAPI() || highlightMode === 'auto' && isSafari();
  const lines = useMemo(() => {
    const l = code.split('\n');
    if (l.length > 1 && l[l.length - 1] === '') {
      l.pop();
    }
    return l;
  }, [code]);
  const tokenLines = useTokenLines(code, language, customTokenizer);
  const highlightSet = useMemo(() => highlightLines ? new Set(highlightLines) : null, [highlightLines]);
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      // Swapping the button's aria-label alone isn't reliably announced by
      // screen readers, so confirm the copy via a polite live region.
      announce('Copied');
      onCopy?.();
      // Restart the reset timer on every copy — otherwise a rapid re-copy
      // is reverted early by the previous click's timer.
      if (copyResetTimerRef.current != null) {
        clearTimeout(copyResetTimerRef.current);
      }
      copyResetTimerRef.current = setTimeout(() => {
        copyResetTimerRef.current = null;
        setCopied(false);
      }, 2000);
    } catch {
      // Clipboard failures leave the copied state unchanged.
    }
  }, [code, onCopy, announce]);
  const sizeStyle = size === 'sm' ? styles.sizeSm : styles.sizeMd;
  // Digits in the largest line number — sizes the gutter column width.
  const maxLineDigits = String(lines.length).length;
  const languageLabel = hasLanguageLabel && language !== 'plaintext' ? language : null;
  const showHeader = title != null || languageLabel != null;
  const canCollapse = isCollapsible && lines.length >= collapsibleThreshold;
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Links the collapsible header to the code region it shows/hides so assistive
  // tech can move from the button to its controlled content (disclosure
  // pattern). The region stays mounted when collapsed (CSS grid animation), so
  // this is always a resolvable reference — aria-controls can be unconditional.
  const regionId = useId();
  const scrollContainerRef = useRef(null);
  const scrollStyle = maxHeight ? {
    maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight
  } : undefined;
  const copyIcon = /*#__PURE__*/_jsx(Icon, {
    icon: copied ? 'check' : 'copy',
    size: "sm",
    color: "inherit"
  });
  const copyButtonEl = hasCopyButton ? /*#__PURE__*/_jsx("button", {
    type: "button",
    onClick: e => {
      // Stop propagation so copying does not toggle the collapsible header.
      e.stopPropagation();
      void handleCopy();
    },
    "aria-label": copied ? t('@astryx.codeBlock.copied') : t('@astryx.codeBlock.copyCode'),
    ...{
      0: {
        className: "x3nfvp2 x6s0dn4 xl56j7k x9epnlk x3kzqx6 xx3sua9 xjbqb8w xe9uy6x x1mjzq1q x1ypdohk x14ju556"
      },
      1: {
        className: "x3nfvp2 x6s0dn4 xl56j7k x9epnlk x3kzqx6 xx3sua9 xjbqb8w xe9uy6x x1mjzq1q x1ypdohk x14ju556 x10l6tqk xctzyg x1n93rvh"
      }
    }[!!!showHeader << 0],
    children: copyIcon
  }) : null;
  const headerEl = showHeader ? /*#__PURE__*/_jsxs("div", {
    ...{
      0: {
        className: "x78zum5 x6s0dn4 x1qughib x1pzlopt xzyc27v x7wzq59 x13vifvy x1vjfegm xce4md1"
      },
      1: {
        className: "x78zum5 x6s0dn4 x1qughib x1pzlopt xzyc27v x7wzq59 x13vifvy x1vjfegm xce4md1 x92x3c3 x1q0q8m5 xw8gpjh"
      }
    }[!!hasLineNumbers << 0],
    children: [/*#__PURE__*/_jsx("div", {
      role: canCollapse ? 'button' : undefined,
      tabIndex: canCollapse ? 0 : undefined,
      "aria-expanded": canCollapse ? !isCollapsed : undefined,
      "aria-controls": canCollapse ? regionId : undefined,
      onClick: canCollapse ? () => setIsCollapsed(prev => !prev) : undefined,
      onKeyDown: canCollapse ? e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsCollapsed(prev => !prev);
        }
      } : undefined,
      ...{
        0: {
          className: "x78zum5 x6s0dn4 x98rzlu xeuugli x1717udv xjbqb8w x1heor9g xln7xf2 x1yc453h"
        },
        1: {
          className: "x78zum5 x6s0dn4 x98rzlu xeuugli x1717udv xjbqb8w x1heor9g xln7xf2 x1yc453h x1ypdohk x87ps6o x17nn4n9 x1wfwxd8 x7s97pk"
        }
      }[!!canCollapse << 0],
      children: /*#__PURE__*/_jsxs("span", {
        ...{
          className: "x78zum5 x6s0dn4 xzye2dw x141an7d x9m5x89 x1e4wzip x1mjzq1q x1ghz6dp x1ltkj2j"
        },
        children: [title, title && languageLabel ? ' — ' : '', languageLabel, canCollapse && /*#__PURE__*/_jsx("span", {
          ...{
            0: {
              className: "x3nfvp2 x6s0dn4 xl56j7k x2lah0s x6jxa94 x1v9usgg x1mjzq1q x11xpdln xuedmi6 xlr8y92"
            },
            1: {
              className: "x3nfvp2 x6s0dn4 xl56j7k x2lah0s x6jxa94 x1v9usgg x1mjzq1q x11xpdln xuedmi6 xlr8y92 x19jd1h0"
            }
          }[!!isCollapsed << 0],
          children: /*#__PURE__*/_jsx(Icon, {
            icon: "chevronDown",
            size: "xsm",
            color: "inherit"
          })
        })]
      })
    }), copyButtonEl]
  }) : null;
  const codeBody = /*#__PURE__*/_jsx("div", {
    ref: scrollContainerRef
    // The scroll container is keyboard-focusable so keyboard users can
    // scroll long or wide code that overflows the viewport. Uses
    // role="group" (not "region") so multiple code blocks on a page don't
    // create duplicate same-named landmarks (axe: landmark-unique).
    ,
    tabIndex: 0,
    role: "group",
    "aria-label": languageLabel ?? t('@astryx.codeBlock.code'),
    ...mergeProps({
      className: "xw2csxc x1odjw0f"
    }, {
      style: scrollStyle
    }),
    children: /*#__PURE__*/_jsx("div", {
      ...{
        0: {
          className: "x78zum5 x1pshirs"
        },
        1: {
          className: "x78zum5 x1pshirs x1ajqlyv"
        }
      }[!!(showHeader && !hasLineNumbers) << 0],
      children: useSpans ? /*#__PURE__*/_jsx(SpanCodeContent, {
        lines: lines,
        tokenLines: tokenLines,
        highlightSet: highlightSet,
        isWrapped: isWrapped,
        sizeStyle: sizeStyle,
        hasLineNumbers: hasLineNumbers,
        maxDigits: maxLineDigits
      }) : /*#__PURE__*/_jsx(RangeCodeContent, {
        lines: lines,
        tokenLines: tokenLines,
        highlightSet: highlightSet,
        isWrapped: isWrapped,
        sizeStyle: sizeStyle,
        hasLineNumbers: hasLineNumbers,
        maxDigits: maxLineDigits
      })
    })
  });
  const block = /*#__PURE__*/_jsxs("pre", {
    ref: ref,
    ...mergeProps(themeProps('codeblock', {
      size,
      language,
      container
    }), stylex.props(styles.root, dynamicStyles.width(widthProp), containerStyles[container], xstyle), className, style),
    ...props,
    children: [headerEl, canCollapse ? /*#__PURE__*/_jsx("div", {
      id: regionId,
      ...{
        0: {
          className: "xrvj5dj x1tu4anv x1qn9uv2 x80gvsz xlr8y92"
        },
        1: {
          className: "xrvj5dj x1qn9uv2 x80gvsz xlr8y92 xihq33y"
        }
      }[!!isCollapsed << 0],
      children: /*#__PURE__*/_jsx("div", {
        ...{
          className: "xb3r6kr x2lwn1j"
        },
        children: codeBody
      })
    }) : codeBody, !showHeader && copyButtonEl]
  });
  return syntaxTheme ? /*#__PURE__*/_jsx(SyntaxTheme, {
    theme: syntaxTheme,
    children: block
  }) : block;
}
CodeBlock.displayName = 'CodeBlock';